/**
 * `<GuideMeProvider>` — the integration point a host wires once,
 * supplying every host-bound dependency the React components consume.
 *
 * Components grab the adapter via {@link useGuideMeAdapter}. There is no
 * global; calling the hook outside a provider throws so missing wiring
 * fails loudly rather than producing a silently-broken Guide Me surface.
 *
 * Nested {@link GuideMeFloatingLauncherContext} tracks whether the user
 * dismissed the idle floating launcher (bubble + orb); hosts use
 * {@link useGuideMeHeaderRecallVisible} for a header recall affordance.
 */

import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
  type ReactNode,
} from 'react';
import type { GuideMeAdapter } from './types';

const GuideMeContext = createContext<GuideMeAdapter | null>(null);

export interface GuideMeFloatingLauncherContextValue {
  readonly floatingLauncherDismissed: boolean;
  readonly dismissFloatingLauncher: () => void;
  readonly restoreFloatingLauncher: () => void;
}

const GuideMeFloatingLauncherContext =
  createContext<GuideMeFloatingLauncherContextValue | null>(null);

/**
 * localStorage key for the user's "close" choice on the floating launcher
 * (the orb / teaser bubble). Persisted so a reload doesn't undo the
 * dismiss; cleared back to "not dismissed" via the header recall button.
 */
const FLOATING_LAUNCHER_DISMISSED_STORAGE_KEY =
  'empowernow.guide-me.floating-launcher.dismissed';

function readFloatingLauncherDismissed(): boolean {
  if (typeof window === 'undefined') return false;
  try {
    return (
      window.localStorage.getItem(FLOATING_LAUNCHER_DISMISSED_STORAGE_KEY) === '1'
    );
  } catch (err) {
    // Don't swallow silently: surface storage failures (private mode,
    // disabled storage, quota) so the regression is debuggable instead
    // of presenting as "dismiss never sticks".
    // eslint-disable-next-line no-console
    console.warn(
      '[guide-me-react] Failed to read floating launcher state from localStorage:',
      err,
    );
    return false;
  }
}

function writeFloatingLauncherDismissed(dismissed: boolean): void {
  if (typeof window === 'undefined') return;
  try {
    if (dismissed) {
      window.localStorage.setItem(FLOATING_LAUNCHER_DISMISSED_STORAGE_KEY, '1');
    } else {
      window.localStorage.removeItem(FLOATING_LAUNCHER_DISMISSED_STORAGE_KEY);
    }
  } catch (err) {
    // eslint-disable-next-line no-console
    console.warn(
      '[guide-me-react] Failed to persist floating launcher state to localStorage:',
      err,
    );
  }
}

export interface GuideMeProviderProps {
  readonly adapter: GuideMeAdapter;
  readonly children: ReactNode;
}

export function GuideMeProvider({ adapter, children }: GuideMeProviderProps) {
  const [floatingLauncherDismissed, setFloatingLauncherDismissed] = useState<boolean>(
    () => readFloatingLauncherDismissed(),
  );

  useEffect(() => {
    writeFloatingLauncherDismissed(floatingLauncherDismissed);
  }, [floatingLauncherDismissed]);

  const dismissFloatingLauncher = useCallback(() => {
    setFloatingLauncherDismissed(true);
  }, []);
  const restoreFloatingLauncher = useCallback(() => {
    setFloatingLauncherDismissed(false);
  }, []);

  const floatingLauncher = useMemo<GuideMeFloatingLauncherContextValue>(
    () => ({
      floatingLauncherDismissed,
      dismissFloatingLauncher,
      restoreFloatingLauncher,
    }),
    [floatingLauncherDismissed, dismissFloatingLauncher, restoreFloatingLauncher],
  );

  return (
    <GuideMeContext.Provider value={adapter}>
      <GuideMeFloatingLauncherContext.Provider value={floatingLauncher}>
        {children}
      </GuideMeFloatingLauncherContext.Provider>
    </GuideMeContext.Provider>
  );
}

/**
 * Hook used inside the package to read the host adapter. Throws when
 * called outside a `<GuideMeProvider>` — that's a wiring bug, not a
 * recoverable runtime state.
 */
export function useGuideMeAdapter(): GuideMeAdapter {
  const ctx = useContext(GuideMeContext);
  if (!ctx) {
    throw new Error(
      '[guide-me-react] useGuideMeAdapter() called outside <GuideMeProvider>. ' +
      'Wrap your app with <GuideMeProvider adapter={…}> before rendering Guide Me components.',
    );
  }
  return ctx;
}

/**
 * Idle floating launcher (orb + teaser bubble) dismiss / restore.
 * Must be used under `<GuideMeProvider>`.
 */
export function useGuideMeFloatingLauncher(): GuideMeFloatingLauncherContextValue {
  const ctx = useContext(GuideMeFloatingLauncherContext);
  if (!ctx) {
    throw new Error(
      '[guide-me-react] useGuideMeFloatingLauncher() called outside <GuideMeProvider>. ' +
      'Floating launcher state lives on the same provider as the adapter.',
    );
  }
  return ctx;
}

/**
 * True when an idle Guide Me launcher could be shown for this route/plugin
 * (same gate as {@link GuideMeRoot} before it renders the hint pill).
 */
export function useGuideMeIdleLauncherEligible(): boolean {
  const adapter = useGuideMeAdapter();
  const { pluginId, pathname } = adapter;
  const guideEntry = useMemo(() => adapter.getGuideEntry(pluginId), [adapter, pluginId]);
  const tourSummaries = useMemo(() => adapter.getToursForPlugin(pluginId), [adapter, pluginId]);
  const pillSummary = useMemo(() => adapter.findTourFor(pluginId, pathname), [adapter, pluginId, pathname]);
  const hasMenu = !!guideEntry && tourSummaries.length > 0;
  return !!pillSummary || hasMenu;
}

/**
 * When true, hosts should show a compact control (e.g. header “?”) so the
 * user can restore the floating launcher after dismissing it.
 */
export function useGuideMeHeaderRecallVisible(): boolean {
  const { floatingLauncherDismissed } = useGuideMeFloatingLauncher();
  const eligible = useGuideMeIdleLauncherEligible();
  return floatingLauncherDismissed && eligible;
}
